import type { ChecklistExtractionStatus, ChecklistProposalCoverage, ChecklistResearchSummary } from "@/api/plan_review";

/** Poll cadence for idle/background checks (extractions, checklist lists). */
export const PLAN_REVIEW_POLL_MS_IDLE = 2500;

/** Longest wait between checklist list polls once requests start failing. */
export const CHECKLIST_LIST_POLL_MAX_MS = 60_000;
/**
 * Visible time per in-flight stretch before the manager stops checking on
 * its own, even while a run is still heartbeating: long enough for an
 * 80-page document with research on a slow model day.
 */
export const CHECKLIST_LIST_POLL_CEILING_MS = 3 * 60 * 60 * 1000;

/**
 * Next delay for the checklist list poll, given how long it has been
 * polling in a visible tab. Successful polls keep the idle cadence for the
 * first ten minutes, since the stage line is what the planner is watching,
 * then ease off for a long run; each consecutive failure doubles the wait,
 * capped, so a down server is not hammered. Returns null at the ceiling.
 */
export function nextChecklistListPollMs(consecutiveFailures: number, visibleMs: number): number | null {
  if (visibleMs >= CHECKLIST_LIST_POLL_CEILING_MS) return null;
  if (consecutiveFailures > 0) {
    return Math.min(PLAN_REVIEW_POLL_MS_IDLE * 2 ** Math.min(consecutiveFailures, 6), CHECKLIST_LIST_POLL_MAX_MS);
  }
  if (visibleMs < 10 * 60 * 1000) return PLAN_REVIEW_POLL_MS_IDLE;
  if (visibleMs < 60 * 60 * 1000) return PLAN_REVIEW_POLL_MS_IDLE * 2;
  return PLAN_REVIEW_POLL_MS_IDLE * 4;
}

/** Statuses the manager keeps polling on; retrying is a bounded parser/model retry, still in flight. */
export const CHECKLIST_EXTRACTION_IN_FLIGHT_STATUSES: ReadonlyArray<ChecklistExtractionStatus["status"]> = [
  "pending",
  "processing",
  "retrying",
];

/**
 * A run that has shown no sign of life for this long (no heartbeat, no
 * start, nothing since the upload) gets a Discard escape hatch in the
 * dialog. The server judges it against
 * PlanReviewChecklistExtraction::STALE_AFTER and sends `quiet`; this
 * mirror is only for a payload without it. Whether a run is actually dead
 * is `stalled`, also the server's call.
 */
export const CHECKLIST_EXTRACTION_QUIET_MS = 10 * 60 * 1000;

/** The server says the run had a worker and lost it. */
export function checklistExtractionStalled(status: ChecklistExtractionStatus): boolean {
  return status.stalled === true;
}

export function checklistExtractionQuiet(status: ChecklistExtractionStatus, now = Date.now()): boolean {
  if (!CHECKLIST_EXTRACTION_IN_FLIGHT_STATUSES.includes(status.status)) return false;
  if (typeof status.quiet === "boolean") return status.quiet;
  const lastSeen = status.last_heartbeat_at ?? status.started_at ?? status.created_at;
  if (!lastSeen) return false;
  return now - Date.parse(lastSeen) > CHECKLIST_EXTRACTION_QUIET_MS;
}

/**
 * What became of the long bulleted notes the model skipped (ENG-2100): the
 * ones a second check put back as requirements, the ones it left out as
 * instructions, and the ones it never got to. A proposal from before the
 * check existed has no notes_check and keeps the older wording.
 */
function describeSkippedNotes(leftOut: number, check: ChecklistProposalCoverage["notes_check"]): string {
  const noun = (count: number) => `bulleted note${count === 1 ? "" : "s"}`;
  const was = (count: number) => (count === 1 ? "was" : "were");
  if (!check) {
    return leftOut > 0
      ? ` ${leftOut} ${noun(leftOut)} read as instructions rather than requirements and ${was(leftOut)} left out.`
      : "";
  }
  if (check.status === "failed") {
    return leftOut > 0
      ? ` ${leftOut} ${noun(leftOut)} read as instructions and ${was(leftOut)} left out; the check on ${leftOut === 1 ? "it" : "them"} could not run, so read ${leftOut === 1 ? "it" : "them"} in the original.`
      : "";
  }
  const restored = check.restored;
  const judgedNotes = check.checked - restored;
  const unchecked = check.unchecked;
  const sentences: string[] = [];
  if (restored > 0) {
    sentences.push(
      `${restored} of the added lines ${restored === 1 ? "is a long bulleted note" : "are long bulleted notes"} that a second check judged ${restored === 1 ? "a requirement" : "requirements"}.`
    );
  }
  if (judgedNotes > 0) {
    sentences.push(`${judgedNotes} ${noun(judgedNotes)} ${was(judgedNotes)} checked and left out as instructions.`);
  }
  if (unchecked > 0) {
    sentences.push(
      `${unchecked} ${judgedNotes > 0 ? "more " : ""}${noun(unchecked)} ${was(unchecked)} left out without being checked, so read ${unchecked === 1 ? "it" : "them"} in the original.`
    );
  }
  return sentences.map((sentence) => ` ${sentence}`).join("");
}

/** The notes the second check read and left out, with its reason for each, so the planner can overrule it. */
export function checklistNotesLeftOut(coverage: ChecklistProposalCoverage | null | undefined) {
  return (coverage?.notes_check?.verdicts ?? []).filter((verdict) => verdict.verdict === "note");
}

/** The sentences of running text the second check read and left out (ENG-2101), with its reason for each. */
export function checklistSentencesLeftOut(coverage: ChecklistProposalCoverage | null | undefined) {
  return (coverage?.prose_check?.verdicts ?? []).filter((verdict) => verdict.verdict === "note");
}

function joinClauses(clauses: string[]): string {
  if (clauses.length <= 1) return clauses.join("");
  if (clauses.length === 2) return `${clauses[0]} and ${clauses[1]}`;
  return `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
}

/**
 * What became of the sentences of running text that read like
 * requirements (ENG-2101): how many were found, and of those how many the
 * proposed rows already cover, how many a second check put back or left
 * out, and how many it never got to (past its cap, or it failed). Empty
 * when the document had none, and on proposals from before the check.
 */
function describeProseSentences(coverage: ChecklistProposalCoverage): string {
  const found = coverage.prose_candidates ?? 0;
  if (found <= 0) return "";
  const was = (count: number) => (count === 1 ? "was" : "were");
  const them = (count: number) => (count === 1 ? "it" : "them");
  const matched = coverage.prose_matched ?? 0;
  const restored = coverage.prose_restored ?? 0;
  const check = coverage.prose_check;
  const judgedOut = check && check.status !== "failed" ? Math.max(check.checked - check.restored, 0) : 0;
  const unchecked = Math.max((coverage.prose_left_out ?? 0) - judgedOut, 0);
  const clauses: string[] = [];
  if (matched > 0) clauses.push(`${matched} ${matched === 1 ? "is" : "are"} covered by the proposed rows`);
  if (restored > 0) {
    clauses.push(
      `${restored} ${was(restored)} added after a second check judged ${them(restored)} ${restored === 1 ? "a requirement" : "requirements"}`
    );
  }
  if (judgedOut > 0) clauses.push(`${judgedOut} ${was(judgedOut)} checked and left out as instructions`);
  const lead = `${found} sentence${found === 1 ? "" : "s"} in the document's running text read${found === 1 ? "s" : ""} like ${found === 1 ? "a requirement" : "requirements"}`;
  let text = clauses.length > 0 ? ` ${lead}: ${joinClauses(clauses)}.` : ` ${lead}.`;
  if (unchecked > 0) {
    text +=
      check?.status === "failed"
        ? ` ${unchecked} ${was(unchecked)} left out because the check on ${them(unchecked)} could not run, so read ${them(unchecked)} in the original.`
        : ` ${unchecked} ${was(unchecked)} left out without being checked, so read ${them(unchecked)} in the original.`;
  }
  return text;
}

/**
 * One plain sentence on how the proposal squares with the document's own
 * item lines, so the planner knows what was checked and what was not.
 */
export function describeChecklistCoverage(coverage: ChecklistProposalCoverage | null | undefined): string | null {
  if (!coverage) return null;
  const shortfall = coverage.parser_shortfall ?? 0;
  const marks = `${shortfall} more ${coverage.marker_witness === "vision" ? "item" : "checkbox"} mark${shortfall === 1 ? "" : "s"}`;
  let shortfallSentence = "";
  if (shortfall > 0) {
    const witness = coverage.marker_witness === "vision" ? "The pages show" : "The PDF's own text layer has";
    shortfallSentence = ` ${witness} ${marks} than the parser read, so check these rows against the original.`;
  } else if (coverage.vision_check === "failed") {
    shortfallSentence = " The item marks on this document could not be counted to check the parser, so compare these rows with the original.";
  }
  const dropped = coverage.dropped_rows ?? 0;
  const droppedSentence =
    dropped > 0
      ? ` ${dropped} proposed row${dropped === 1 ? " was" : "s were"} left out for holding only a blank to fill in or an answer choice such as Yes or No.`
      : "";
  const tail = `${describeProseSentences(coverage)}${droppedSentence}${shortfallSentence}`;
  if (coverage.structure === "none" || coverage.source_rows === 0) {
    return `No list structure was found in this document, so the rows come from reading its text. Review them carefully.${tail}`;
  }
  const lines = `${coverage.source_rows} item line${coverage.source_rows === 1 ? "" : "s"}`;
  const notes = coverage.notes_left_out ?? 0;
  const notesSentence = describeSkippedNotes(notes, coverage.notes_check);
  if (coverage.added_from_source === 0) {
    const covered = notes > 0 ? `All but ${notes} of the ${lines}` : `All ${lines}`;
    return `${covered} in the document are covered.${notesSentence}${tail}`;
  }
  return `${coverage.added_from_source} of the document's ${lines} ${coverage.added_from_source === 1 ? "was" : "were"} added word for word because the model skipped ${coverage.added_from_source === 1 ? "it" : "them"}.${notesSentence}${tail}`;
}

/**
 * One plain sentence on the look-for guidance the research pass wrote for
 * the rows, so the planner knows how much of it rests on local documents.
 */
export function describeChecklistResearch(
  research: ChecklistResearchSummary | null | undefined,
  rowCount: number
): string | null {
  if (!research) return null;
  if (research.status === "failed" || research.researched === 0) {
    return "Look-for guidance could not be written for these rows. You can add it by hand before confirming.";
  }
  const failedSearches = research.retrieval_errors ?? 0;
  const searchSentence =
    failedSearches > 0
      ? ` This municipality's documents could not be searched for ${failedSearches} ${failedSearches === 1 ? "row" : "rows"}, so check ${failedSearches === 1 ? "that one" : "those"} against your own code.`
      : "";
  const wrote =
    research.researched >= rowCount
      ? "Look-for guidance was written for every row"
      : `Look-for guidance was written for ${research.researched} of ${rowCount} rows`;
  if (research.sourced === 0) {
    return `${wrote}, none of it from local documents, so check it against your own code.${searchSentence}`;
  }
  const sourced =
    research.sourced === research.researched
      ? "all of it citing local documents"
      : `${research.sourced} ${research.sourced === 1 ? "row cites" : "rows cite"} local documents`;
  // The pass calls itself partial when it could cite a local document for
  // no more than half the rows, or left some unwritten. Every row without
  // a local source, written or not, is one the planner checks by hand,
  // and the rows whose search failed are among them rather than a
  // separate instruction.
  const unsourced = Math.max(rowCount, research.researched) - research.sourced;
  if (research.status === "partial" && unsourced > 0) {
    const count = unsourced === 1 ? "one that does not" : `${unsourced} that do not`;
    const failed =
      failedSearches > 0
        ? `, including the ${failedSearches === 1 ? "one" : failedSearches} this municipality's documents could not be searched for`
        : "";
    return `${wrote}, ${sourced}. Check the ${count} against your own code${failed}.`;
  }
  return `${wrote}, ${sourced}.${searchSentence}`;
}

/** Checklist extraction stages, aligned with PlanReview::ExtractChecklistJob heartbeats. */
export const CHECKLIST_EXTRACTION_STAGE_LABELS: Record<string, string> = {
  parse_file: "Reading the document",
  propose_rows: "Listing every requirement",
  propose_rows_retry: "Retrying the model",
  normalize_rows: "Preparing rows",
  check_skipped_notes: "Checking skipped notes",
  // The same check when it also reads sentences of running text no row
  // covers (ENG-2101).
  check_skipped_lines: "Checking skipped notes and sentences",
  // Batch index, emitted by runs that started before research_rows_checked
  // existed; kept so they keep rendering as batches until they finish.
  research_rows: "Researching local requirements",
  research_rows_retry: "Retrying the model",
};

/**
 * The research pass is most of the wait, so its line says what has already
 * happened (the checklist is read) and what the wait is for (each
 * requirement checked against the municipality's own code), with the
 * count moving per row. The count leads once it is moving: the list row
 * truncates, and a static preamble would hide the one part that changes.
 * "research_rows_checked:7/14" is rows checked out of all rows (ENG-2139).
 */
function describeResearchStage(checked: number, total: number): string {
  const requirements = `${total} requirement${total === 1 ? "" : "s"}`;
  if (checked <= 0) {
    return `Read ${requirements} from your checklist. Now comparing ${total === 1 ? "it" : "them"} against your municipal code`;
  }
  if (checked >= total) {
    return `Compared ${total === 1 ? "the" : "all"} ${requirements} from your checklist against your municipal code, now writing look-for guidance`;
  }
  return `Comparing ${checked} of ${requirements} from your checklist against your municipal code`;
}

/**
 * The stage without its moving count, for a screen reader's live region:
 * announced when the run enters a new stage, not on every row it checks.
 */
export function describeChecklistExtractionPhase(stage: string | null | undefined): string {
  if (!stage) return "Extracting checklist";
  const [key] = stage.split(":");
  if (key === "research_rows_checked") return "Comparing your checklist against your municipal code";
  return CHECKLIST_EXTRACTION_STAGE_LABELS[key] ?? key.replace(/_/g, " ");
}

/** "propose_rows:2/3" reads as "Listing every requirement (part 2 of 3)". */
export function describeChecklistExtractionStage(stage: string | null | undefined): string {
  if (!stage) return "Extracting checklist";
  const [key, progress] = stage.split(":");
  const label = CHECKLIST_EXTRACTION_STAGE_LABELS[key] ?? key.replace(/_/g, " ");
  if (!progress) return label;
  const [index, count] = progress.split("/").map(Number);
  if (key === "research_rows_checked" && Number.isFinite(index) && count > 0) return describeResearchStage(index, count);
  return `${label} (part ${index} of ${count})`;
}
